// Copyright (c) Ping. All rights reserved.
//
// The popover body for every guided tour, rendered as Reactour's
// `components.Content`. Themed with the app's design tokens (NativeWind
// classes resolve inside the portal because dark mode is class-based on
// <html>), so it matches the shell in light and dark. Reactour's own
// badge / dots / nav / close chrome is disabled; this card owns the
// progress indicator and the Back / Next / Skip controls.

import { Pressable, Text, View } from 'react-native';

import { useTour } from '@reactour/tour';

import { useGuidedTour } from './GuidedTourContext';
import { dismissTour } from './tourDismiss';

export function GuidedTourCard(): JSX.Element | null {
  const { currentStep, setCurrentStep, setIsOpen } = useTour();
  const { tourId, steps, markSeenOnDismiss } = useGuidedTour();

  const step = steps[currentStep];
  if (step === undefined) {
    return null;
  }

  const total = steps.length;
  const isFirst = currentStep <= 0;
  const isLast = currentStep >= total - 1;
  const progressPct = Math.round(((currentStep + 1) / total) * 100);

  const goBack = (): void => setCurrentStep((s) => Math.max(0, s - 1));
  const goNext = (): void => {
    if (isLast) {
      dismissTour(tourId, setIsOpen, markSeenOnDismiss);
    } else {
      setCurrentStep((s) => s + 1);
    }
  };
  const skip = (): void => dismissTour(tourId, setIsOpen, markSeenOnDismiss);

  return (
    <View className="w-[320px] max-w-[86vw] flex flex-col gap-3">
      {/* Progress + skip */}
      <View className="flex flex-row items-center justify-between">
        <Text className="text-[11px] font-semibold uppercase tracking-wider text-light-text-tertiary dark:text-white/45">
          Step {currentStep + 1} of {total}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Skip the tour"
          onPress={skip}
          className="px-1 py-0.5 rounded-md hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
        >
          <Text className="text-[13px] font-medium text-light-text-secondary dark:text-white/55">
            Skip
          </Text>
        </Pressable>
      </View>

      {/* Progress bar */}
      <View className="h-1 w-full rounded-full overflow-hidden bg-black/[0.06] dark:bg-white/[0.10]">
        <View
          className="h-full rounded-full bg-light-accent-brand dark:bg-dark-accent-brand"
          style={{ width: `${progressPct}%` }}
        />
      </View>

      {/* Copy */}
      <Text className="text-[16px] font-bold leading-[22px] text-light-text-primary dark:text-white">
        {step.title}
      </Text>
      <Text className="text-[14px] leading-[20px] text-light-text-secondary dark:text-white/70">
        {step.body}
      </Text>

      {/* Controls */}
      <View className="flex flex-row items-center justify-between pt-1">
        {isFirst ? (
          <View />
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Previous step"
            onPress={goBack}
            className="px-3 py-2 rounded-lg border border-light-border-subtle dark:border-white/[0.12] hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
          >
            <Text className="text-[14px] font-semibold text-light-text-primary dark:text-white">
              Back
            </Text>
          </Pressable>
        )}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={isLast ? 'Finish the tour' : 'Next step'}
          onPress={goNext}
          className="px-4 py-2 rounded-lg bg-light-accent-brand dark:bg-dark-accent-brand active:opacity-80"
        >
          <Text className="text-[14px] font-semibold text-white">{isLast ? 'Done' : 'Next'}</Text>
        </Pressable>
      </View>
    </View>
  );
}
